
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User as FirebaseUser } from 'firebase/auth';
import { User } from '../types';
import { 
  loginUser, 
  logoutUser, 
  getCurrentUserData, 
  updateLastLogin, 
  onAuthStateChange,
  isSessionExpired,
  clearLoginTimestamp
} from '../firebase/auth';

interface AuthContextType {
  currentUser: User | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
  loading: boolean;
  currentMode: 'inputter' | 'operations' | 'authorizer';
  setMode: (mode: 'inputter' | 'operations' | 'authorizer') => void;
  currentModule: 'revenue' | 'expenses' | null;
  setModule: (module: 'revenue' | 'expenses' | null) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentMode, setCurrentMode] = useState<'inputter' | 'operations' | 'authorizer'>('inputter');
  const [currentModule, setCurrentModule] = useState<'revenue' | 'expenses' | null>(null);

  // Set mode based on user role when user changes
  useEffect(() => {
    if (currentUser) {
      // Set default mode based on user role
      if (currentUser.role === 'authorizer') {
        setCurrentMode('authorizer');
      } else if (currentUser.role === 'operations') {
        setCurrentMode('operations');
      } else if (currentUser.role === 'inputter') {
        setCurrentMode('inputter');
      }
    }
  }, [currentUser]);

  useEffect(() => {
    const unsubscribe = onAuthStateChange(async (firebaseUser: FirebaseUser | null) => {
      if (firebaseUser) {
        try {
          // Get user data from Firestore
          const userData = await getCurrentUserData(firebaseUser.uid);
          if (userData) {
            // Check if session has expired (only if timestamp exists)
            if (isSessionExpired()) {
              console.log('Session expired. Logging out...');
              clearLoginTimestamp();
              await logoutUser();
              setCurrentUser(null);
              setLoading(false);
              return;
            }

            setCurrentUser(userData);
            // Update last login
            await updateLastLogin(firebaseUser.uid);
          }
        } catch (error) {
          console.error('Error getting user data:', error);
        }
      } else {
        setCurrentUser(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const user = await loginUser(email, password);
      setCurrentUser(user);
    } catch (error: any) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      await logoutUser();
      setCurrentUser(null);
      setCurrentMode('inputter'); // Reset mode to default on logout
    } catch (error: any) {
      throw error;
    }
  };

  // Wrapper for setMode that prevents non-authorizer users from changing mode
  const handleSetMode = (mode: 'inputter' | 'operations' | 'authorizer') => {
    // Only allow mode change if user is an authorizer
    if (currentUser?.role === 'authorizer') {
      setCurrentMode(mode);
    }
    // Non-authorizer users cannot change their mode
  };

  const value = {
    currentUser,
    login,
    logout,
    isAuthenticated: !!currentUser,
    loading,
    currentMode,
    setMode: handleSetMode,
    currentModule,
    setModule: setCurrentModule
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
